import instance from "@/api/axiosInstance";

const images = new Map();
const queue = [];
const MAX_DOWNLOADS = 2;
const MAX_BYTES = 64 * 1024 * 1024;
const MAX_ENTRIES = 160;
const CACHE_IDLE_MS = 5 * 60 * 1000;
let running = 0;
let cachedBytes = 0;

function discard(entry) {
  clearTimeout(entry.timer);
  if (images.get(entry.key) === entry) images.delete(entry.key);
  if (entry.url) {
    URL.revokeObjectURL(entry.url);
    cachedBytes -= entry.bytes;
    entry.url = "";
  }
}

function trim() {
  const unused = [...images.values()]
    .filter((entry) => entry.refs === 0 && entry.state === "ready")
    .sort((a, b) => a.used - b.used);
  for (const entry of unused) {
    if (cachedBytes <= MAX_BYTES && images.size <= MAX_ENTRIES) break;
    discard(entry);
  }
}

function pump() {
  while (running < MAX_DOWNLOADS && queue.length) {
    queue.sort((a, b) => a.priority - b.priority);
    const entry = queue.shift();
    if (entry.state !== "queued") continue;
    entry.state = "loading";
    running++;
    instance
      .get(
        `/data-download/images/${encodeURIComponent(entry.imageId)}/${
          entry.variant
        }`,
        {
          responseType: "blob",
          timeout: 60000,
          signal: entry.controller.signal,
        }
      )
      .then((response) => {
        if (entry.state !== "loading") throw new Error("图片请求已取消");
        if (!response.data.type.startsWith("image/"))
          throw new Error("图片读取失败");
        entry.bytes = response.data.size || 0;
        entry.url = URL.createObjectURL(response.data);
        cachedBytes += entry.bytes;
        entry.state = "ready";
        entry.resolve(entry.url);
        trim();
      })
      .catch((error) => {
        discard(entry);
        entry.state = "failed";
        entry.reject(error);
      })
      .finally(() => {
        running--;
        pump();
      });
  }
}

function imageKey(account, imageId, variant, version) {
  return JSON.stringify([String(account), String(imageId), variant, version]);
}

export function getCachedDataImageUrl(account, imageId, variant, version = "") {
  return images.get(imageKey(account, imageId, variant, version))?.url || "";
}

/** Account-scoped, bounded cache; leave connections free for navigation and API requests. */
export function acquireDataImage(
  account,
  imageId,
  variant,
  version = "",
  priority = variant.endsWith("-thumb") ? 1 : 0
) {
  const key = imageKey(account, imageId, variant, version);
  let entry = images.get(key);
  if (!entry) {
    entry = {
      key,
      imageId,
      variant,
      refs: 0,
      url: "",
      bytes: 0,
      state: "queued",
      controller: new AbortController(),
      priority,
    };
    entry.promise = new Promise((resolve, reject) => {
      entry.resolve = resolve;
      entry.reject = reject;
    });
    images.set(key, entry);
    queue.push(entry);
  }
  clearTimeout(entry.timer);
  entry.priority = Math.min(entry.priority, priority);
  entry.used = Date.now();
  entry.refs++;
  pump();
  let released = false;
  return {
    get url() {
      return entry.url;
    },
    promise: entry.promise,
    release() {
      if (released) return;
      released = true;
      if (--entry.refs !== 0) return;
      if (entry.state === "queued" || entry.state === "loading") {
        entry.state = "cancelled";
        entry.controller.abort();
        discard(entry);
        entry.reject(new Error("图片请求已取消"));
        const index = queue.indexOf(entry);
        if (index >= 0) queue.splice(index, 1);
      } else if (entry.state === "ready") {
        entry.timer = setTimeout(() => {
          if (!entry.refs) discard(entry);
        }, CACHE_IDLE_MS);
        trim();
      }
    },
  };
}
